import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropdownMenuContextType {
    open: boolean;
    setOpen: (open: boolean) => void;
    disabled?: boolean;
    triggerRef: React.RefObject<HTMLElement>;
}

interface DropdownMenuProps {
    children: React.ReactNode;
    disabled?: boolean;
}

interface DropdownMenuTriggerProps {
    children: React.ReactNode;
    className?: string;
    asChild?: boolean;
}

interface DropdownMenuContentProps {
    children: React.ReactNode;
    className?: string;
    align?: 'start' | 'center' | 'end';
    side?: 'top' | 'bottom' | 'left' | 'right';
}

interface DropdownMenuItemProps {
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    destructive?: boolean;
}

interface DropdownMenuSeparatorProps {
    className?: string;
}

// Context
const DropdownMenuContext = createContext<DropdownMenuContextType | null>(null);

const useDropdownMenuContext = () => {
    const context = useContext(DropdownMenuContext);
    if (!context) {
        throw new Error('DropdownMenu components must be used within a DropdownMenu');
    }
    return context;
};

// Main DropdownMenu component
const DropdownMenu: React.FC<DropdownMenuProps> = ({ children, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLElement>(null!);

    return (
        <DropdownMenuContext.Provider value={{ open, setOpen, disabled, triggerRef }}>
            <div className="relative inline-block text-left">
                {children}
            </div>
        </DropdownMenuContext.Provider>
    );
};

// DropdownMenu Trigger
const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({
                                                                     children,
                                                                     className,
                                                                     asChild = false
                                                                 }) => {
    const { open, setOpen, disabled, triggerRef } = useDropdownMenuContext();

    if (asChild && children) {
        return React.cloneElement(children as React.ReactElement, {
            ref: (el: HTMLElement) => {
                if (el) {
                    (triggerRef as React.MutableRefObject<HTMLElement>).current = el;
                }
            },
            onClick: () => !disabled && setOpen(!open),
            'aria-expanded': open,
            'aria-haspopup': true,
            disabled
        } as any);
    }

    return (
        <button
            ref={(el) => {
                if (el) {
                    (triggerRef as React.MutableRefObject<HTMLElement>).current = el;
                }
            }}
            type="button"
            onClick={() => !disabled && setOpen(!open)}
            className={cn(
                "inline-flex items-center justify-center rounded-md p-1 text-sm font-medium transition-colors",
                "hover:bg-background-light-elevated dark:hover:bg-background-dark-elevated",
                "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
                "disabled:pointer-events-none disabled:opacity-50",
                className
            )}
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup={true}
        >
            {children || <MoreVertical className="h-4 w-4" />}
        </button>
    );
};

// Helper function to calculate position
const calculatePosition = (
    triggerElement: HTMLElement,
    align: 'start' | 'center' | 'end',
    side: 'top' | 'bottom' | 'left' | 'right'
) => {
    const triggerRect = triggerElement.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    let top = 0;
    let left = 0;

    // Calculate based on side
    switch (side) {
        case 'bottom':
            top = triggerRect.bottom + scrollY + 4;
            break;
        case 'top':
            top = triggerRect.top + scrollY - 4;
            break;
        case 'right':
            left = triggerRect.right + scrollX + 4;
            top = triggerRect.top + scrollY;
            break;
        case 'left':
            left = triggerRect.left + scrollX - 4;
            top = triggerRect.top + scrollY;
            break;
    }

    // Calculate based on align
    if (side === 'top' || side === 'bottom') {
        switch (align) {
            case 'start':
                left = triggerRect.left + scrollX;
                break;
            case 'center':
                left = triggerRect.left + scrollX + triggerRect.width / 2;
                break;
            case 'end':
                left = triggerRect.right + scrollX;
                break;
        }
    }

    return { top, left };
};

// DropdownMenu Content
const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
                                                                     children,
                                                                     className,
                                                                     align = 'end',
                                                                     side = 'bottom'
                                                                 }) => {
    const { open, setOpen, triggerRef } = useDropdownMenuContext();
    const contentRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (open && triggerRef.current) {
            const pos = calculatePosition(triggerRef.current, align, side);
            setPosition(pos);
        }
    }, [open, align, side, triggerRef]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                contentRef.current &&
                !contentRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        const handleScroll = () => {
            if (open && triggerRef.current) {
                const pos = calculatePosition(triggerRef.current, align, side);
                setPosition(pos);
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleScroll);

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleEscape);
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, [open, setOpen, triggerRef, align, side]);

    if (!open) return null;

    const adjustedLeft = side === 'top' || side === 'bottom' ?
        position.left + (align === 'center' ? 0 : align === 'end' ? 0 : 0) :
        position.left;

    const adjustedTop = side === 'left' || side === 'right' ?
        position.top :
        side === 'top' ? position.top : position.top;

    const content = (
        <div
            ref={contentRef}
            className={cn(
                "fixed z-[9999] min-w-[12rem] max-w-[16rem] overflow-hidden rounded-md border border-border-light dark:border-border-dark-subtle bg-background-light dark:bg-background-dark-secondary shadow-lg",
                "animate-in fade-in-0 zoom-in-95 duration-100",
                side === 'bottom' && "slide-in-from-top-2",
                side === 'top' && "slide-in-from-bottom-2",
                side === 'left' && "slide-in-from-right-2",
                side === 'right' && "slide-in-from-left-2",
                className
            )}
            style={{
                top: adjustedTop,
                left: adjustedLeft,
                transform: (side === 'top' || side === 'bottom') && align === 'end' ? 'translateX(-100%)' :
                    (side === 'top' || side === 'bottom') && align === 'center' ? 'translateX(-50%)' :
                        side === 'top' ? 'translateY(-100%)' : 'none'
            }}
        >
            <div className="p-1">
                {children}
            </div>
        </div>
    );

    // Use portal to render outside of any containers
    return createPortal(content, document.body);
};

// DropdownMenu Item
const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
                                                               children,
                                                               className,
                                                               onClick,
                                                               disabled = false,
                                                               destructive = false
                                                           }) => {
    const { setOpen } = useDropdownMenuContext();

    const handleClick = () => {
        if (!disabled) {
            onClick?.();
            setOpen(false);
        }
    };

    return (
        <div
            className={cn(
                "relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none transition-colors whitespace-nowrap",
                "focus:bg-background-light-elevated dark:focus:bg-background-dark-elevated focus:text-text-primary-light dark:focus:text-text-primary-dark",
                "hover:bg-background-light-elevated dark:hover:bg-background-dark-elevated hover:text-text-primary-light dark:hover:text-text-primary-dark",
                destructive && "text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-950/50 hover:bg-red-50 dark:hover:bg-red-950/50",
                disabled && "pointer-events-none opacity-50",
                className
            )}
            onClick={handleClick}
        >
            {children}
        </div>
    );
};

// DropdownMenu Separator
const DropdownMenuSeparator: React.FC<DropdownMenuSeparatorProps> = ({ className }) => {
    return (
        <div
            className={cn(
                "-mx-1 my-1 h-px bg-border-light dark:bg-border-dark-subtle",
                className
            )}
        />
    );
};

export {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
};